import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  Search,
  SearchIconWrapper,
  StyledInputBase,
} from "../components/CustomMUI.js";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Popper,
} from "@mui/material";
import { useDebounce } from "../hooks/useDebounce.js";

// Extra clearance scrolled past the field's right edge once expanded, so it
// doesn't end up pinned flush against the screen edge.
const SCROLL_OFFSET_PX = 16;

// How far the page may scroll before open suggestions are dismissed. Not zero:
// opening the keyboard, or the results behind re-filtering, can nudge the page.
const SUGGESTIONS_SCROLL_DISMISS_PX = 40;

// Finds the nearest ancestor that actually scrolls horizontally (the header
// AppBar has overflowX: auto). Walked at runtime rather than passed in, since
// this component is dropped into several differently-structured headers.
function findHorizontalScrollParent(el) {
  let node = el?.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    if (
      /(auto|scroll)/.test(style.overflowX) &&
      node.scrollWidth > node.clientWidth
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Header search field. Two modes:
 *
 * - Plain (default): filters by title as you type (debounced).
 * - Picker (when `suggestionSections` is passed): typing only reports the text
 *   via onQueryChange, and the parent answers with suggestions to pick from.
 *   Nothing is applied until one is picked, after which the text clears so
 *   another can be picked straight away.
 *
 * @param {string} searchTerm - Plain mode: the applied term (initial text)
 * @param {(term: string) => void} onSearchTermChange - Plain mode: debounced
 *   as you type
 * @param {boolean} [alignEnd] Set when this is the last element in a header row
 *   with nothing after it. On narrow viewports the field keeps a gap matching
 *   the Container gutter so it never meets the screen edge when the row is
 *   scrolled; on sm+ it sits flush and relies on the gutter itself.
 * @param {string} [placeholder]
 * @param {Array<{ title?: string, items: Array<{ key: string|number,
 *   label: string, secondary?: string,
 *   icon?: React.ReactNode }> }>} [suggestionSections]
 *   Picker mode: suggestions for the current text, in sections. Enter picks
 *   the highlighted item, or the first if none is.
 * @param {(query: string) => void} [onQueryChange] - Picker mode: every change
 *   to the text, unthrottled
 * @param {(item: object) => void} [onSuggestionSelect] - Picker mode
 * @param {() => void} [onFocus]
 *
 * Ref: exposes focusWithText(text = "") — focuses the field, optionally
 * pre-filled (picker mode: suggestions show once there's text). Call it
 * synchronously inside a tap handler: iOS only raises the keyboard for a
 * focus() made during the user's gesture.
 */
const SearchFilter = forwardRef(function SearchFilter(
  {
    searchTerm,
    onSearchTermChange,
    alignEnd = false,
    placeholder = "Filter by title",
    suggestionSections,
    onQueryChange,
    onSuggestionSelect,
    onFocus,
  },
  ref,
) {
  const isPicker = suggestionSections !== undefined;

  const [inputValue, setInputValue] = React.useState(
    isPicker ? "" : searchTerm || "",
  );
  const [isFocused, setIsFocused] = React.useState(false);
  // Hidden by Escape or by scrolling away; any new typing brings them back
  const [dismissed, setDismissed] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);

  // The field lives in a horizontally-scrollable header and grows to the right on
  // focus; at the right edge that growth spills into the hidden overflow.
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const expanded = isFocused || !!inputValue;

  const debouncedSearchTermChange = useDebounce(onSearchTermChange, 300);

  const showSuggestions =
    isPicker && isFocused && !dismissed && !!inputValue.trim();
  const sections = showSuggestions
    ? suggestionSections.filter((section) => section.items.length > 0)
    : [];
  // Flattened, for keyboard navigation across sections
  const items = sections.flatMap((section) => section.items);
  const suggestionsOpen = items.length > 0;

  // Picker mode: the text is a query, so it's reported as it changes
  const setText = (text) => {
    setInputValue(text);
    if (isPicker) onQueryChange?.(text.trim());
  };

  useImperativeHandle(ref, () => ({
    focusWithText: (text = "") => {
      inputRef.current?.focus();
      setText(text);
      setDismissed(false);
    },
  }));

  // Picker mode: a tap anywhere else leaves the field (dropping unpicked text).
  // Not left to the browser's own blur — iOS doesn't blur on taps outside
  // inputs, and posters cancel their touches' default (see PosterTile).
  // Capture phase, so nothing underneath can stop it first.
  useEffect(() => {
    if (!isPicker || !isFocused) return undefined;
    const handlePointerDown = (event) => {
      if (
        searchRef.current?.contains(event.target) ||
        suggestionsRef.current?.contains(event.target)
      ) {
        return;
      }
      inputRef.current?.blur();
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [isPicker, isFocused]);

  // The header hides on scroll, which would leave the suggestions floating
  // with nothing above them — dismiss them once the page has really moved.
  useEffect(() => {
    if (!suggestionsOpen) return undefined;
    const startY = window.scrollY;
    const handleScroll = () => {
      if (Math.abs(window.scrollY - startY) > SUGGESTIONS_SCROLL_DISMISS_PX) {
        setDismissed(true);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [suggestionsOpen]);

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
    // Wait for the input's width-expand transition (~300ms) to settle, then
    // compute the exact scroll delta ourselves — relying on scrollIntoView's
    // own "is it visible enough" heuristic proved unreliable across nested
    // scroll containers, so we scroll the container directly and add an
    // explicit pixel offset past the field's edge.
    setTimeout(() => {
      const field = searchRef.current;
      const container = findHorizontalScrollParent(field);
      if (!field || !container) return;

      const fieldRight = field.getBoundingClientRect().right;
      const containerRight = container.getBoundingClientRect().right;
      const delta = fieldRight - containerRight + SCROLL_OFFSET_PX;

      if (delta > 0) {
        container.scrollBy({ left: delta, behavior: "smooth" });
      }
    }, 350);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setActiveIndex(-1);
    // Picker mode: unpicked text was never applied, so don't leave it looking
    // as if it had been
    if (isPicker) setText("");
  };

  const handleChange = (event) => {
    setText(event.target.value);
    setDismissed(false);
    setActiveIndex(-1);
    if (!isPicker) debouncedSearchTermChange(event.target.value);
  };

  const handleClear = () => {
    setText("");
    if (!isPicker) onSearchTermChange("");
  };

  const selectSuggestion = (item) => {
    onSuggestionSelect(item);
    setText("");
    setActiveIndex(-1);
  };

  const handleKeyDown = useCallback(
    (event) => {
      if (suggestionsOpen && event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % items.length);
      } else if (suggestionsOpen && event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
      } else if (suggestionsOpen && event.key === "Enter") {
        event.preventDefault();
        selectSuggestion(items[Math.max(activeIndex, 0)]);
      } else if (event.key === "Escape") {
        // First Escape closes the suggestions, the next clears and leaves
        if (suggestionsOpen) {
          setDismissed(true);
          return;
        }
        handleClear();
        event.target.blur();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSearchTermChange, suggestionsOpen, items, activeIndex],
  );

  const field = (
    <Search
      ref={searchRef}
      highlight={!!inputValue}
      // xs: gap so the field clears the screen edge when scrolled.
      // sm+: rely on the Container gutter instead of adding to it.
      sx={alignEnd ? { mr: { xs: 2, sm: 0 } } : undefined}
    >
      <SearchIconWrapper highlight={!!inputValue}>
        <SearchIcon />
      </SearchIconWrapper>

      <StyledInputBase
        id="search-library-by-title"
        inputRef={inputRef}
        placeholder={inputValue ? "" : placeholder}
        // The expanded width (10ch, 15ch on md+; see StyledInputBase) is a
        // minimum: a longer placeholder widens it rather than being cut off
        sx={{
          "& .MuiInputBase-input:focus, & .MuiInputBase-input:not(:placeholder-shown)":
            {
              width: {
                xs: `max(10ch, ${placeholder.length}ch)`,
                md: `max(15ch, ${placeholder.length}ch)`,
              },
            },
        }}
        inputProps={{
          "aria-label": "search",
          autoComplete: "off",
          ...(isPicker && {
            role: "combobox",
            "aria-expanded": suggestionsOpen,
            "aria-controls": "search-suggestions",
          }),
        }}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        endAdornment={
          isFocused || inputValue ? (
            <InputAdornment position="end" sx={{ pr: 1 }}>
              <IconButton
                aria-label="clear search"
                onClick={handleClear}
                edge="end"
                size="small"
                sx={{ visibility: inputValue ? "visible" : "hidden" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
      />

      {/* Portalled, so the header's horizontal overflow can't clip it */}
      <Popper
        open={suggestionsOpen}
        anchorEl={searchRef.current}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
      >
        <Paper
          ref={suggestionsRef}
          elevation={0}
          sx={{
            width: 280,
            maxWidth: "calc(100vw - 32px)",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          }}
        >
          <List
            id="search-suggestions"
            role="listbox"
            dense
            sx={{ maxHeight: "50vh", overflowY: "auto" }}
          >
            {sections.map((section) => (
              <React.Fragment key={section.title ?? "untitled"}>
                {section.title && (
                  <ListSubheader sx={{ lineHeight: "32px" }}>
                    {section.title}
                  </ListSubheader>
                )}
                {section.items.map((item) => {
                  const index = items.indexOf(item);
                  return (
                    <ListItemButton
                      key={item.key}
                      role="option"
                      selected={index === activeIndex}
                      // Keeps focus (and the mobile keyboard) in the field
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectSuggestion(item)}
                    >
                      {item.icon && (
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {item.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={item.label}
                        secondary={item.secondary}
                        primaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                  );
                })}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Popper>
    </Search>
  );

  if (!alignEnd) return field;

  return (
    // Browsers drop the trailing margin of the last flex item once a row
    // overflows, so the field's margin above is only honoured while it sits
    // inside this wrapper rather than directly in the scrollable Toolbar.
    <Box sx={{ display: "flex", flexShrink: 0 }}>
      {field}

      {/* sm+ only, while expanded: the field has no trailing margin there, so
          this gives the scroll container genuine extra scrollWidth for
          handleFocus to scroll into (on xs the margin already provides it).
          The non-breaking space is load-bearing: browsers drop *empty* trailing
          boxes from the horizontal scrollable-overflow region, so a bare
          width-only Box adds no scrollWidth at all. */}
      {expanded && (
        <Box
          aria-hidden
          sx={{
            display: { xs: "none", sm: "block" },
            width: SCROLL_OFFSET_PX,
            flexShrink: 0,
          }}
        >
          {" "}
        </Box>
      )}
    </Box>
  );
});

export default SearchFilter;
